import { AccountingAmexPayment, CardType, DepositSourceType, DepositType, InvalidParsedOrder, ParsedOrder, PayfrontAccountingPayment, SourceBusiness } from "../../AdminClasses";
import { IconName } from "react-frontier2";
import { PaymentMethods } from "@arema/components/Classes";
import { dehashPaymentOrder } from "../../AdminUtil";
import { fromUnix } from "@arema/components/Util";
import moment from "moment";
import csv from 'csvtojson';
import API from "../../API";


type ConciliaText<T> = { [x in keyof T]: string }[];

export enum ConciliaFile{
	CHEQUES = 1,
	CONEKTA = 2,
	OPENPAY = 3,
	BANORTE_PIN = 4,
	SANTANDER_PIN = 5,
	SANTANDER_WEB = 6,
	AMEX_WEB = 7,
}

export type ParseResponse = {
	error?: boolean,
	code?: number | string,
	orders?: ParsedOrder[],
	invalid?: InvalidParsedOrder[],
	file?: number,
}

var fixNumber = (num: string)=>{
	if(!num) return null;
	return parseFloat(num.replace(/[^0-9\.\-]/gi, ''));
}

var depositTypeLocale = (type: DepositType)=>{
	switch(type){
		case 1: return 'Venta';
		case 10: return 'Reembolso';
		case 20: return 'Contracargo';
		default: return '???'
	}
}

var depositTypeMethod = (type: ConciliaFile)=>{
	switch(type){
		case ConciliaFile.CHEQUES:
		case ConciliaFile.SANTANDER_WEB:
		case ConciliaFile.AMEX_WEB: return [PaymentMethods.PAYFRONT];
		case ConciliaFile.CONEKTA: return [PaymentMethods.CONEKTA_OXXO];
		case ConciliaFile.OPENPAY: return [PaymentMethods.OPENPAY_CARD, PaymentMethods.OPENPAY_SPEI];
		case ConciliaFile.BANORTE_PIN:
		case ConciliaFile.SANTANDER_PIN: return [PaymentMethods.PDV_PINPAD_AREMA];
		default: return null;
	}
}

var readFile = (file: File)=>{
	return new Promise<string>((resolve, reject)=>{
		const reader = new FileReader();
		reader.addEventListener('load', e=>{
			return resolve(e.target.result as string);
		});
		reader.readAsText(file);
	})
}


var parseConcilia = async (type: ConciliaFile, text: string[], date_deposit: number=null) : Promise<ParseResponse>=>{
	var orders : ParsedOrder[] = [], invalid : InvalidParsedOrder[] = [];
	var filetype = FileTypeList.find(a=>a.type===type);
	if(!filetype){
		return { error: true, code: 100, file: null };
	}
	for(var t=0; t<text.length; t++){
		var file_text = await filetype.fileTransform(text[t]);
		var res = await filetype.parseFn(file_text as any, date_deposit);
		if(res.error){
			res.file = t;
			return res;
		}else if(res.error===false){
			if(res.orders) orders.push(...res.orders);
			if(res.invalid) invalid.push(...res.invalid);
		}
	}
	return { error: false, code: 0, file: null, orders, invalid };
}

// =====================================
// PAYWORKS CHEQUES
// =====================================
interface PayworksFileData{
	'Control Interno Comercio': string,
	'Importe': string,
	'F.Abono': string,
	'Leyenda': 'DEVOLUCIÓN' | 'CCARGO VTA' | 'VENTA',
	'P_TipoCta': 'Débito' | 'Crédito'
}
var parsePayworks = async (data: ConciliaText<PayworksFileData>) : Promise<ParseResponse>=>{
	var orders: ParsedOrder[] = [], invalid : InvalidParsedOrder[] = [];
	for(let o of data){
		if(!o["Control Interno Comercio"] || !o["Importe"] || !o["F.Abono"] || !o["Leyenda"]){
			return { error: true, code: 101 }
		}

		var res = dehashPaymentOrder(o["Control Interno Comercio"]);
		var total = fixNumber(o["Importe"]);

		if(Number.isNaN(total)){
			return { error: true, code: 103 };
		}

		var date_deposit = moment(o["F.Abono"], 'DD/MM/YYYY');
		if(!date_deposit.isValid()) return { error: true, code: 104 };
		date_deposit.set('hour', 12);

		var deposit_type_raw = o["Leyenda"].toUpperCase();
		var deposit_type = deposit_type_raw==='DEVOLUCIÓN' ? DepositType.REFUND : (deposit_type_raw==='CCARGO VTA' ? DepositType.CHARGEBACK : DepositType.DEPOSIT);
		if(deposit_type!==DepositType.DEPOSIT){
			total = Math.abs(total) * -1;
		}

		var new_order : InvalidParsedOrder = {
			// order_id: (res?.order_id || null),
			// payment_id: (res?.payment_id || null),
			deposit_type,
			payment_method: PaymentMethods.PAYFRONT,
			external_id: o["Control Interno Comercio"],
			amount: total,
			card_type: o.P_TipoCta==='Crédito' ? CardType.CREDIT : (o.P_TipoCta==='Débito' ? CardType.DEBIT : CardType.UNKNOWN),
			date_deposit: date_deposit.unix()
		}
		if(res){
			orders.push({
				order_id: res.order_id,
				payment_id: res.payment_id,
				...new_order,
			});
		}else{
			invalid.push(new_order);
		}
	}

	return { error: false, code: 0, orders, invalid };
}

// =====================================
// SANTANDER WEB
// =====================================
interface SantanderWebData{
	order_hash: string,
	term: string,
	amount: string,
	date_deposit: string,
	afiliacion: string,
	card_type: 'C' | 'D',
	type: 'DEVOLUCION' | 'CCARGO' | 'VENTA',
}
var parseSantanderWeb = async (data: ConciliaText<SantanderWebData>, date_deposit: number) : Promise<ParseResponse>=>{
	var orders: ParsedOrder[] = [], invalid: InvalidParsedOrder[] = [];
	for(var o of data){
		if(!o.order_hash || !o.amount || !o.date_deposit || !o.type) return { error: true, code: 101 };
		var res = dehashPaymentOrder(o.order_hash);
		
		var total = fixNumber(o.amount)
		if(Number.isNaN(total)){
			console.log(o);
			return { error: true, code: 103 };
		}

		var dtdep = date_deposit ? moment.unix(date_deposit) : moment(o.date_deposit, 'DD/MM/YYYY');
		if(!dtdep.isValid()) return { error: true, code: 104 };
		dtdep.set('hour', 12);

		var deposit_type_raw = o.type.toUpperCase();
		var deposit_type = (deposit_type_raw==='DEVOLUCION' || deposit_type_raw==='CANCELACION') ? DepositType.REFUND : (deposit_type_raw==='CCARGO' ? DepositType.CHARGEBACK : DepositType.DEPOSIT);
		if(deposit_type!==DepositType.DEPOSIT){
			total = Math.abs(total)*-1;
		}

		var new_order : InvalidParsedOrder = {
			deposit_type,
			payment_method: (o.afiliacion && o.term!=='0004') ? PaymentMethods.PDV_PINPAD_GETNET : PaymentMethods.PAYFRONT,
			external_id: `${o.order_hash}${deposit_type!==DepositType.DEPOSIT ? `_DT${deposit_type}` : ''}`,
			amount: total,
			card_type: o.card_type==='C' ? CardType.CREDIT : o.card_type==='D' ? CardType.DEBIT : CardType.UNKNOWN,
			date_deposit: dtdep.unix()
		}
		if(res){
			orders.push({
				order_id: res.order_id,
				payment_id: res.payment_id,
				...new_order
			})
		}else{
			invalid.push(new_order);
		}
	}
	return { error: false, code: 0, orders, invalid };
}

// =====================================
// CONEKTA
// =====================================
interface ConektaFileData{
	id_cargo: string,
	monto: string,
	metadata_columns: string,
}
var parseConekta = async (data: ConciliaText<ConektaFileData>, date_deposit: number) : Promise<ParseResponse>=>{
	var orders : ParsedOrder[] = [], invalid : InvalidParsedOrder[] = [];;
	for(var o of data){
		if(!o.id_cargo || !o.monto || !o.metadata_columns) return { error: true, code: 101 };
		var metadata : { payment_hash?: string, [x: string]: string } = {}
		for(var k of o.metadata_columns.split(' | ')){
			var [key, val] = k.split(' : ');
			metadata[key] = val;
		}
		var order_id : number = null, payment_id : number = null;
		if(metadata.payment_hash){
			var hash = dehashPaymentOrder(metadata.payment_hash);
			if(hash){
				order_id = hash.order_id;
				payment_id = hash.payment_id;
			}
		}

		var new_order : InvalidParsedOrder = {
			deposit_type: DepositType.DEPOSIT,
			payment_method: PaymentMethods.CONEKTA_OXXO,
			external_id: o.id_cargo,
			amount: parseFloat(o.monto),
			date_deposit: date_deposit
		}
		if(order_id && payment_id){
			orders.push({
				order_id,
				payment_id,
				...new_order
			});
		}else{
			invalid.push(new_order);
		}
	}
	return { error: false, code: 0, orders, invalid };
}

// =====================================
// OPENPAY
// =====================================
interface OpenPayFileData{
	transaction_id: string,
	status: 'COMPLETED' | 'IN_PROGRESS',
	description: string,
	amount: string,
	method: 'BANK_ACCOUNT' | 'CARD' | 'OPENPAY',
	category: 'CHARGE' | 'OPENPAY_PAYMENT' | 'REFUND' | 'CHARGEBACK',
}
var parseOpenPay = async (data: ConciliaText<OpenPayFileData>, date_deposit: number): Promise<ParseResponse>=>{
	var orders : ParsedOrder[] = [], invalid : InvalidParsedOrder[] = [];
	for(var o of data){
		if(o.category==='OPENPAY_PAYMENT' || o.method==='OPENPAY') continue;
		var hash = dehashPaymentOrder(o.description);
		var amount = fixNumber(o.amount);
		var payment_method = o.method==='CARD' ? PaymentMethods.OPENPAY_CARD : PaymentMethods.OPENPAY_SPEI;
		var deposit_type = o.category==='REFUND' ? DepositType.REFUND : (o.category==='CHARGEBACK' ? DepositType.CHARGEBACK : DepositType.DEPOSIT);
		if(deposit_type!==DepositType.DEPOSIT){
			amount = Math.abs(amount)*-1;
		}
		if(!hash){
			invalid.push({
				external_id: o.transaction_id,
				date_deposit,
				amount,
				payment_method,
				deposit_type,
			})
		}else{
			orders.push({
				amount,
				date_deposit,
				deposit_type,
				external_id: o.transaction_id,
				order_id: hash.order_id,
				payment_id: hash.payment_id,
				payment_method,
			});
		}
	}
	return { error: false, code: 0, orders, invalid };
}

// =====================================
// PAYFRONT ACCOUNTING
// =====================================
var parsePayfrontAccounting = async (data: PayfrontAccountingPayment[], date_deposit: number, payment_method: PaymentMethods) : Promise<ParseResponse>=>{
	var orders : ParsedOrder[] = [], invalid : InvalidParsedOrder[] = [];
	for(var i of data){
		var hash = dehashPaymentOrder(i.external_id);
		var dep : InvalidParsedOrder = {
			external_id: `AMX-${i.payment_id}${i.total<0 ? `-R${fromUnix(date_deposit).toFormat('DDMMYY')}` : ''}`,
			date_deposit,
			amount: i.total,
			payment_method: payment_method,
			deposit_type: i.total<0 ? DepositType.REFUND : DepositType.DEPOSIT,
			card_type: CardType.CREDIT,
		}
		if(!hash){
			invalid.push(dep);
		}else{
			orders.push({
				...dep,
				order_id: hash.order_id,
				payment_id: hash.payment_id,
			});
		}
	}
	return { error: false, code: 0, orders, invalid };
}

// =====================================
// AMEX
// =====================================
interface AMEXFileData{
	date: string,
	amount: string,
	reference: string,
	card: string,
	type: 'Sale' | 'Credit',
	date_paid: string,
}
var parseAmex = async (data: ConciliaText<AMEXFileData>, date_deposit: number): Promise<ParseResponse>=>{
	var payments : AccountingAmexPayment[] = [];
	console.log(data);
	for(var i of data){
		var areg = (/(?<n>\()?(?<c>[A-z]{3})\$ ?(?<a>[0-9\,\.]+)/gi).exec(i.amount);
		if(!areg || !areg.groups?.a){
			console.log("INVALID AREG", i, areg);
			continue;
		}
		var amt = fixNumber(areg.groups.a) * (areg.groups.n ? -1 : 1);

		var breg = (/(?<b>[0-9]{6})X+(?<d>[0-9]{4})/gi).exec(i.card);
		if(!breg || !breg.groups.b || !breg.groups.d){
			console.log("INVALID BREG", i, breg);
			continue;
		}

		payments.push({
			card_bin: parseInt(breg.groups.b),
			card_digits: parseInt(breg.groups.d),
			reference: i.reference,
			total: amt,
		});
	}
	if(payments.length==0){
		return { error: false, code: 0, orders: [], invalid: [] };
	}
	try{
		var res = await API.getAmexAccounting(payments);
		if(res.error) return { error: true, code: res.code };
		return parsePayfrontAccounting(res.data, date_deposit, PaymentMethods.PAYFRONT_AMEX);
	}catch(err){
		return { error: true, code: 100 };
	}
}

// =====================================
// FILE TYPE LIST
// =====================================
export interface ConciliaFileType{
	name: string,
	type: ConciliaFile,
	iconName?: IconName,
	iconBrand?: boolean,
	askDate?: boolean,
	sources: PaymentMethodSource[],
	fileTransform: (text: string)=>Promise<{ [x: string]: string }[]>,
	parseFn: <T>(data: ConciliaText<null>, date_deposit: number)=>Promise<ParseResponse>
}
export interface PaymentMethodSource{
	source_id: number,
	business_id: SourceBusiness,
	source_name?: string
}

type SourceKeys = 'AREMA' | 'TICKETLOCK';
type SourceNames = 'BANORTE' | 'SANTANDER' | 'AMEX' | 'CONEKTA' | 'OPENPAY';
const PaymentSources : {
	[x in SourceKeys]: {
		[k in SourceNames]: PaymentMethodSource
	}
} = {
	AREMA: {
		BANORTE: { source_id: 1, business_id: SourceBusiness.AREMA },
		SANTANDER: { source_id: 10, business_id: SourceBusiness.AREMA },
		AMEX: { source_id: 20, business_id: SourceBusiness.AREMA },
		CONEKTA: { source_id: 200, business_id: SourceBusiness.AREMA },
		OPENPAY: { source_id: 300, business_id: SourceBusiness.AREMA },
	},
	TICKETLOCK: {
		BANORTE: { source_id: 2, business_id: SourceBusiness.TICKETLOCK },
		SANTANDER: { source_id: 11, business_id: SourceBusiness.TICKETLOCK },
		AMEX: { source_id: 21, business_id: SourceBusiness.TICKETLOCK },
		CONEKTA: { source_id: 201, business_id: SourceBusiness.TICKETLOCK },
		OPENPAY: { source_id: 301, business_id: SourceBusiness.TICKETLOCK },
	}
}

export const FileTypeList : ConciliaFileType[] = [{
	name: 'Payworks Cheques',
	iconName: 'credit-card',
	type: ConciliaFile.CHEQUES,
	sources: [PaymentSources.AREMA.BANORTE, PaymentSources.TICKETLOCK.BANORTE],
	fileTransform: async text=>{
		var conv = await csv({ delimiter: '|', flatKeys: true }).fromString(text);
		return conv as any;
	},
	parseFn: parsePayworks,
}, {
	name: 'Santander MIT',
	iconName: 'credit-card',
	type: ConciliaFile.SANTANDER_WEB,
	askDate: true,
	sources: [PaymentSources.AREMA.SANTANDER, PaymentSources.TICKETLOCK.SANTANDER],
	fileTransform: async text=>{
		var conv = await csv({ delimiter: '|', flatKeys: true, noheader: true, headers: [
			'ref', 'term', 'order_hash', 'client', 'afiliacion', 'plan', 'field2', 'field3', 'field4', 
			'name', 'auth_code', 'client2', 'business', 'amount', 'currency', 'transaction_date', 
			'transaction_time', 'card_type', 'card_brand', 'bank', 'type', 'date_deposit'
		]}).fromString(text);
		return conv as any;
	},
	parseFn: parseSantanderWeb
}, {
	name: "AMEX",
	iconName: 'cc-amex',
	iconBrand: true,
	type: ConciliaFile.AMEX_WEB,
	askDate: true,
	sources: [PaymentSources.AREMA.AMEX, PaymentSources.TICKETLOCK.AMEX],
	fileTransform: async text=>{
		var lines = text.split(/\r?\n/gi);
		var found = false;
		for(var i=0; i<lines.length; i++){
			if(lines[i].startsWith('Fecha de la')){
				found = true;
				lines = lines.slice(i+1);
			}
		}
		lines = [
			'date,number,commission,amount,reference,card,param0,merchant,invoice,type,param1,param2,param3,param4,terminal,reference_number,date_paid,msi',
			...lines.filter(a=>a.length>3)
		];
		console.log(lines);
		return csv().fromString(lines.join('\n'));
	},
	parseFn: parseAmex
}, {
	name: 'OpenPay',
	iconName: 'credit-card',
	type: ConciliaFile.OPENPAY,
	sources: [PaymentSources.AREMA.OPENPAY],
	askDate: true,
	fileTransform: async text=>csv().fromString(text),
	parseFn: parseOpenPay,
}, {
	name: 'Conekta',
	iconName: 'cash-register',
	type: ConciliaFile.CONEKTA,
	sources: [PaymentSources.AREMA.CONEKTA],
	askDate: true,
	fileTransform: async text=>csv().fromString(text),
	parseFn: parseConekta,
}]

var getPaymentSource = (source_id: number)=>{
	for(var i in PaymentSources){
		for(var k in (PaymentSources as any)[i]){
			if((PaymentSources as any)[i][k].source_id===source_id) return (PaymentSources as any)[i][k] as PaymentMethodSource;
		}
	}
	return null;
}

export {
	depositTypeLocale,
	depositTypeMethod,
	getPaymentSource,	
	parseConcilia,
	readFile
}